// @flow
import React, { useState, useEffect } from 'react';
import noop from 'lodash/noop';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { ModeContextProvider } from '../../common/contexts/mode-context';
import DisabledFormPage from './disabled-form-page';
import CompletedFormPage from './completed-form-page';
import LoadingFormPage from './loading-form-page';
import FormSubmitter from '../form-submitter';
import FileRequestAPI from '../../api/FileRequest';
import Footer from './footer';
import ThemeStyle from '../../common/components/theme-style';
import ThemeHeader from '../../common/components/theme-header';
import {
  MODE,
  STATUS_CODE,
  FILE_REQUEST_STATUS,
  FILE_REQUEST_VISIBILITY,
} from '../../constants';
import PreviewDisabledHeader from './preview-disabled-header';
import type { FileRequest } from '../../types/FileRequest';
import type { Treatments } from '../../types/Treatment';
import resin from '../../common/utils/resin/resin';

import css from './submission-page.module.scss';
import { withSplitTreatment } from '../../common/split-treatment';

type State = {
  fileRequest: ?FileRequest,
  isDisabled: boolean,
  isLoading: boolean,
  numFiles: number,
  submissionSuccessful: boolean,
};
type Props = {
  isPreview?: boolean,
  setTreatmentOverrides?: Treatments => void,
  urlId: string,
};

const initialState: State = {
  fileRequest: null,
  isLoading: true,
  submissionSuccessful: false,
  isDisabled: false,
  numFiles: 0,
};

const SubmissionPage = (props: Props) => {
  const { urlId, isPreview, setTreatmentOverrides = noop } = props;

  const [state, setState] = useState(initialState);
  const {
    isLoading,
    isDisabled,
    fileRequest,
    submissionSuccessful,
    numFiles,
  } = state;

  useEffect(() => {
    if (!isEmpty(urlId)) {
      FileRequestAPI.getFileRequestByUrlId(urlId)
        .then(fetchedFileRequest => {
          setState(s => {
            return {
              ...s,
              isLoading: false,
              isDisabled:
                fetchedFileRequest.status !== FILE_REQUEST_STATUS.ACTIVE,
              fileRequest: fetchedFileRequest,
            };
          });
          const { splitTreatments = {} } = fetchedFileRequest;
          setTreatmentOverrides(splitTreatments);
          const inFrame = window.location !== window.parent.location;
          const component = inFrame
            ? 'submissionpage|iframe'
            : 'submissionpage';
          resin.recordAction({
            action: 'programmatic',
            page: 'file-request-submission',
            feature: 'filerequest',
            component,
            target: 'pageloaded',
            data: {
              file_request_url_id: urlId,
            },
          });
        })
        .catch(e => {
          if (e.response.status === STATUS_CODE.NOT_FOUND) {
            // TODO - Should be NOT FOUND page, but setting it to disabled for now
            setState(s => {
              return { ...s, isLoading: false, isDisabled: true };
            });
          }
        });
    }
  }, [isDisabled, urlId, setTreatmentOverrides]);

  if (isEmpty(urlId) || (isDisabled && !isPreview)) {
    return <DisabledFormPage />;
  }

  if (isLoading) {
    return <LoadingFormPage />;
  }

  const isAuthenticationRequired =
    FILE_REQUEST_VISIBILITY.AUTHENTICATED === get(fileRequest, 'visibility');

  if (submissionSuccessful) {
    return (
      <CompletedFormPage
        isAuthenticationRequired={isAuthenticationRequired}
        numFiles={numFiles}
      />
    );
  }

  const type = get(fileRequest, 'type', MODE.FILE_REQUEST);
  const form = get(fileRequest, 'form');
  const folderId = get(fileRequest, 'folder.id');

  const logo = get(form, 'content.theme.logo.value', '');
  const color = get(form, 'content.theme.colors.primary.value', '');

  const contentClassName = classNames(css.submissionPage, {
    [css.submissionPageWithHeader]: isPreview && isDisabled,
    [css.withTheme]: !!color || !!logo,
    [css.withLogo]: !!logo,
  });

  return (
    <ModeContextProvider mode={type}>
      <div className={css.submissionPageOverlay}>
        <ThemeStyle color={color} wrapperClass={css.submissionPageOverlay} />
        <ThemeHeader color={color} logo={logo} page="builder" />
        {isPreview && isDisabled && <PreviewDisabledHeader />}
        <div className={contentClassName}>
          <div className={css.formSubmitter}>
            <FormSubmitter
              folderId={folderId}
              form={form}
              isAuthenticationRequired={isAuthenticationRequired}
              isPreview={!!isPreview}
              onSuccessfulSubmission={filesCount => {
                setState({
                  ...state,
                  submissionSuccessful: true,
                  numFiles: filesCount,
                });
              }}
              urlId={urlId}
            />
          </div>
          <Footer />
        </div>
      </div>
    </ModeContextProvider>
  );
};

export { SubmissionPage };
export default withSplitTreatment(SubmissionPage);
